import React, { useMemo } from 'react';
import { DataGridPro, GRID_CHECKBOX_SELECTION_COL_DEF, GridCellParams, GridColDef, GridSelectionModel } from '@mui/x-data-grid-pro';
import { Chip } from '@mui/material';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons';
import { CaseworkerAbsenceModel } from '../../../../api-service/clients';
import { FasitDataGridContextToolbar } from '../../../../components/data-grid/fasit-data-grid-context-toolbar';
import { FasitDataGridToolbar } from '../../../../components/data-grid/fasit-data-grid-toolbar';
import { today } from '../../../../utilities/date-helper';
import { ForumGridOverlay } from '../forum-grid-overlay';
import { absenceDay, formatAbsencePeriod, isActiveOrPlanned } from './absence-day';

export interface ICaseworkerAbsenceListProps {
  absences: CaseworkerAbsenceModel[];
  isLoading: boolean;
  hasFailed: boolean;
  isBusy: boolean;
  caseworkerPicker?: React.ReactNode;
  selectedAbsenceIds: string[];
  onSelectionChange: (absenceIds: string[]) => void;
  onCreate: () => void;
  onOpen: (absence: CaseworkerAbsenceModel) => void;
  onDeleteSelected: () => void;
}

interface IAbsenceToolbarProps {
  hasSelection: boolean;
  isBusy: boolean;
  caseworkerPicker?: React.ReactNode;
  onCreate: () => void;
  onDeleteSelected: () => void;
}

function NoAbsences() {
  return <ForumGridOverlay message="Der er ingen aktive eller planlagte fravær." />;
}

function FailedToLoadAbsences() {
  return <ForumGridOverlay message="Fraværet kunne ikke hentes." />;
}

// Declared out here so the grid keeps one toolbar instance across renders.
function AbsenceToolbar(props: IAbsenceToolbarProps) {
  const { hasSelection, isBusy, caseworkerPicker, onCreate, onDeleteSelected } = props;

  if (hasSelection) {
    return (
      <FasitDataGridContextToolbar buttons={[{ title: 'Slet valgte fravær', icon: faTrashCan, onClick: onDeleteSelected, disabledReason: isBusy ? 'Vent venligst' : undefined }]} />
    );
  }
  return (
    <FasitDataGridToolbar
      title="Aktive og planlagte fravær"
      onAddButtonClick={onCreate}
      addButtonTooltip="Opret ny"
      disabled={isBusy}
      customInputBeforeFilter={caseworkerPicker}
      showColumnButton={false}
      showFilterButton={false}
      showDensityButton={false}
    />
  );
}

export function CaseworkerAbsenceList(props: ICaseworkerAbsenceListProps) {
  const { absences, isLoading, hasFailed, isBusy, selectedAbsenceIds, caseworkerPicker, onSelectionChange, onCreate, onOpen, onDeleteSelected } = props;
  const components = useMemo(() => ({ Toolbar: AbsenceToolbar, NoRowsOverlay: hasFailed ? FailedToLoadAbsences : NoAbsences }), [hasFailed]);
  const currentDay = today();
  const upcomingAbsences = absences.filter(isActiveOrPlanned).sort((first, second) => absenceDay(first.startDate).getTime() - absenceDay(second.startDate).getTime());

  const columns: GridColDef[] = [
    {
      field: 'period',
      headerName: 'Periode',
      width: 260,
      sortable: false,
      valueGetter: ({ row }) => formatAbsencePeriod(row),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      sortable: false,
      renderCell: ({ row }) => {
        const isActive = absenceDay(row.startDate) <= currentDay;
        return <Chip size="small" color={isActive ? 'info' : 'default'} label={isActive ? 'Aktivt' : 'Planlagt'} />;
      },
    },
    {
      field: 'text',
      headerName: 'Tekst',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: ({ row }) => <span style={{ whiteSpace: 'pre-line' }}>{row.text}</span>,
    },
  ];

  function handleCellClick(cell: GridCellParams<unknown, CaseworkerAbsenceModel>) {
    if (cell.field !== GRID_CHECKBOX_SELECTION_COL_DEF.field) {
      onOpen(cell.row);
    }
  }

  function handleCellKeyDown(cell: GridCellParams<unknown, CaseworkerAbsenceModel>, event: React.KeyboardEvent) {
    if (event.key === 'Enter' && cell.field !== GRID_CHECKBOX_SELECTION_COL_DEF.field) {
      onOpen(cell.row);
    }
  }

  function handleSelectionModelChange(model: GridSelectionModel) {
    onSelectionChange(model.map(String));
  }

  return (
    <DataGridPro<CaseworkerAbsenceModel>
      rows={upcomingAbsences}
      columns={columns}
      loading={isLoading}
      getRowId={absence => absence.absenceId}
      autoHeight
      getRowHeight={() => 'auto'}
      density="compact"
      disableColumnMenu
      disableSelectionOnClick
      checkboxSelection
      hideFooter
      columnBuffer={columns.length}
      components={components}
      componentsProps={{ toolbar: { hasSelection: selectedAbsenceIds.length > 0, isBusy, caseworkerPicker, onCreate, onDeleteSelected } }}
      selectionModel={selectedAbsenceIds}
      onSelectionModelChange={handleSelectionModelChange}
      onCellClick={handleCellClick}
      onCellKeyDown={handleCellKeyDown}
      sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
    />
  );
}
